import { useState } from 'react';
import { useTheme } from 'next-themes';
import {
  CheckCircle2,
  Sparkles,
  FileUp,
  Search,
  BarChart3,
  PlayCircle,
  BookOpen,
  LayoutGrid,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { MiniFooter } from '../components/MiniFooter';
import orbitLogo from '/images/logo.png';
import orbitDarkLogo from '/images/logo-dark.png';

// Swap in the final walkthrough video (YouTube/Vimeo embed URL or a self-hosted mp4).
// Leave empty until it's ready - the dialog shows a placeholder instead of a broken player.
const WALKTHROUGH_VIDEO_URL = '';

// Documentation lives in-app on the existing Help page.
const DOCUMENTATION_PATH = '/help';

const CAPABILITIES = [
  {
    icon: FileUp,
    title: 'Analyze Your Software',
    description:
      'Upload an SBOM or connect your repositories to discover open-source components across your applications.',
  },
  {
    icon: Search,
    title: 'Identify and Assess Risks',
    description:
      'Let ORbiT8 analyze your components for vulnerabilities, license compliance issues and other policy violations.',
  },
  {
    icon: BarChart3,
    title: 'Take Action With Confidence',
    description:
      'View clear insights, track scores, generate reports and enforce policies to keep your software secure and compliant.',
  },
];

const FAQS = [
  {
    question: 'What does ORbiT8 actually scan?',
    answer:
      'Orbit8 analyzes your software for vulnerabilities, license risks, and compliance issues. Turn complex software data into clear, actionable insights.',
  },
  {
    question: 'Do I need a GitHub token?',
    answer:
      'Only to scan repositories. A fine-grained token with Contents: Read-only is enough, and you can save it under Settings → Personal Access Token.',
  },
  {
    question: 'How are risk scores calculated?',
    answer:
      'Each component gets license, vulnerability, operational and technical-debt scores on a 0-100 scale, rolled up into an overall risk score per application.',
  },
  {
    question: 'What\'s included in the Community Edition?',
    answer:
      'Your personal workgroup with repository scanning, license and vulnerability reporting, and the standard policy set.',
  },
];

export function Welcome({ onContinue }: { onContinue: () => void }) {
  const { theme } = useTheme();
  const [showWalkthrough, setShowWalkthrough] = useState(false);

  const openDocumentation = () => {
    window.open(DOCUMENTATION_PATH, '_blank', 'noopener,noreferrer');
  };

  return (
    // Column layout with the footer pushed to the bottom, so it always sits at the
    // end of the page regardless of how short the content is.
    <div className="min-h-screen bg-background flex flex-col items-center p-4">
      <div className="w-full max-w-6xl flex-1 py-10 space-y-6">
        <div className="text-center space-y-3">
          <img
            src={theme === 'dark' ? orbitDarkLogo : orbitLogo}
            alt="ORbiT8"
            className="h-12 w-auto mx-auto"
          />
          <h1 className="text-3xl font-semibold text-foreground">Welcome to ORbiT8</h1>
          <p className="text-muted-foreground">
            Your email is verified &mdash; your account is ready.
          </p>
        </div>

        <Card className="bg-card shadow-sm rounded-lg border">
          <CardContent className="p-6">
            <div className="flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div className="space-y-1">
                <h2 className="text-lg font-semibold text-foreground">
                  Your personal workgroup is just ahead
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Complete the next few steps to create your personal workgroup and start
                  exploring Orbit8. Your workgroup will be set up with the Community Edition
                  by default.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Two columns so the content uses the full width instead of one tall stack:
            capabilities on the left, FAQ on the right. Collapses below lg. */}
        <div className="grid gap-6 lg:grid-cols-2 items-start">
          <Card className="bg-card shadow-sm rounded-lg border h-full">
            <CardContent className="p-6 space-y-4">
              <div className="space-y-1">
                <h2 className="text-lg font-semibold text-foreground flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-primary mt-1 shrink-0" />
                  Start Securing Your Software Supply Chain with ORbiT8
                </h2>
                <p className="text-sm text-muted-foreground">
                  Three key steps to gain visibility, manage risk, and stay compliant.
                </p>
              </div>

              <div className="space-y-3">
                {CAPABILITIES.map(({ icon: Icon, title, description }) => (
                  <div
                    key={title}
                    className="rounded-lg border bg-muted/30 p-4 flex gap-3 items-start"
                  >
                    <div className="rounded-md bg-primary/10 p-2 shrink-0">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="font-medium text-sm text-foreground">{title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card shadow-sm rounded-lg border h-full">
            <CardContent className="p-6 space-y-2">
              <h2 className="text-lg font-semibold text-foreground">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="w-full">
                {FAQS.map(({ question, answer }) => (
                  <AccordionItem key={question} value={question}>
                    <AccordionTrigger className="text-left text-sm">{question}</AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                      {answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>

        {/* The note sits outside the tile but inside this wrapper, so it keeps the
            tile's width and alignment, stays within the page container and wraps
            instead of overflowing on narrow screens. */}
        <div className="space-y-2">
          <Card className="bg-card shadow-sm rounded-lg border">
            <CardContent className="p-6">
              {/* Hidden until the walkthrough video and public docs URL are ready.
                  Restore this block (and openDocumentation above) to bring them back. */}
              {/* <div className="flex flex-col sm:flex-row gap-3 justify-center mb-4">
                <Button variant="outline" onClick={() => setShowWalkthrough(true)}>
                  <PlayCircle className="w-4 h-4 mr-2" />
                  Watch the 3-min walkthrough
                </Button>
                <Button variant="outline" onClick={openDocumentation}>
                  <BookOpen className="w-4 h-4 mr-2" />
                  Documentation
                </Button>
              </div> */}

              <div className="flex flex-col items-center gap-3 text-center">
                <Button
                  onClick={onContinue}
                  className="h-11 px-6 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg"
                >
                  <LayoutGrid className="w-4 h-4 mr-2" />
                  Continue To Onboarding
                </Button>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>

      <MiniFooter />

      <Dialog open={showWalkthrough} onOpenChange={setShowWalkthrough}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>ORbiT8 in 3 minutes</DialogTitle>
            <DialogDescription>
              A quick tour of scanning a repository and reading your first risk report.
            </DialogDescription>
          </DialogHeader>
          <div className="aspect-video w-full overflow-hidden rounded-lg bg-muted">
            {WALKTHROUGH_VIDEO_URL ? (
              <iframe
                src={WALKTHROUGH_VIDEO_URL}
                title="ORbiT8 walkthrough"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground">
                The walkthrough video will be available shortly.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Welcome;
